import useLatest from "@-/com.npmjs.use-latest";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import type { LearnCardDestination } from "@-/dev.improve.haniverse.lib.learn.destination";
import { prefetchPageNavigatorQueries } from "@-/dev.improve.haniverse.lib.navigator.page";
import { useLearnSummaryPageQueryOptions } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { useCallback } from "react";

/**
 * Prepares a ROUTE destination for the card leaving for it. Nothing is waited for: the summary owns
 * its loading (`LearnSummaryHost` draws its page-shaped fallback), so its query is only warmed,
 * and the last card of a session leaves as soon as its write lands. Review, lesson and slide cards
 * have had their queries prepared by the deck before mounting; leaving the stack needs nothing.
 */
export function usePrepareLearnCardDestination(): (
  destination: LearnCardDestination,
) => Promise<void> {
  const queryClient = useQueryClient();
  const summaryQueryOptionsRef = useLatest(useLearnSummaryPageQueryOptions());

  return useCallback(
    async (destination: LearnCardDestination) => {
      switch (destination.kind) {
        case "summary": {
          void prefetchPageNavigatorQueries(queryClient, [summaryQueryOptionsRef.current]);
          return;
        }
        case "review":
        case "lesson":
        case "slide":
        case "quiz":
        case "exit": {
          return;
        }
      }
    },
    [queryClient, summaryQueryOptionsRef],
  );
}
